export const rpm = {
  workflowTab: {
    readOnlyTitle: 'Read-Only Access',
    readOnlyDescription:
      'Your current role grants you read-only access to the data in this tab. You can view all information but cannot make any changes.'
  },
  configuration: {
    patientSettingsOnlyDescription: 'Only Patient Settings can be changed until the visit starts.'
  },
  addActivity: {
    discardTitle: 'Discard this activity?',
    discardMessage:
      'You have entered notes or a duration that have not been saved. Closing this panel will lose them. Press Save instead if you want to keep this activity.',
    discardConfirm: 'Discard',
    discardCancel: 'Keep editing'
  },
  patientDrawer: {
    fetchLogsErrorMessage: 'Could not fetch text reminder logs',
    initialVisitSetupLabel: 'Visit & Setup',
    profileHeading: 'Profile',
    truentityIdLabel: 'Truentity Id',
    dateOfBirthLabel: 'Date of Birth',
    phoneLabel: 'Phone',
    locationLabel: 'Location',
    genderLabel: 'Gender',
    healthPlanLabel: 'Health Plan',
    dateEnrolledLabel: 'Date Enrolled',
    onboardedOnLabel: 'Onboarded On',
    currentMonthTargetMetricsLabel: "Current Month's Target Metrics",
    monthlyTotalDaysOfReadingsLabel: 'Monthly Total Days of Readings',
    totalTimeLabel: 'Total Time',
    rpmTimeLabel: 'RPM Time',
    ccmTimeLabel: 'CCM Time',
    monthlyTimeSpentLabel: 'Monthly Time Spent'
  },
  activityProgramChart: {
    title: 'Time Spent by Program',
    totalLabel: 'Total',
    minutesUnit: 'min',
    setupPeriodLabel: 'Setup · All time',
    emptyState: 'No care time recorded for this period.'
  },
  myPatientHeader: {
    followUpTasksLabel: 'My Tasks: Follow-up',
    onboardPatientsLabel: 'Onboard Patients'
  },
  myPatientKpiSection: {
    deviceReadingsTrackerTitle: 'Device Readings Tracker',
    careManagementTrackerTitle: 'Care Management Tracker',
    hideKpiLabel: 'Hide Patient KPI',
    viewKpiLabel: 'View Patient KPI'
  },
  myPatientFilterCustomButton: {
    nonDue: 'Non Due',
    completed: 'Completed',
    overdue: 'Overdue',
    upcoming: 'Upcoming',
    toContact: 'To Contact',
    medRecs: 'MedRecs',
    billingPaused: 'Billing Paused',
    medSync: 'Med Sync'
  },
  patientReadingsMobileView: {
    systolicLabel: 'Systolic',
    glucoseLabel: 'Glucose',
    heartRateLabel: 'Heart rate',
    lastDaysLabel: 'Last Days',
    pressureLabel: 'Pressure',
    thisMonthLabel: 'This Month',
    loadingLabel: 'Loading…',
    loadOlderReadingsLabel: 'Load older readings'
  },
  myPatientsUtils: {
    candidatesLabel: 'Candidates',
    priorityCandidatesLabel: 'Priority Candidates',
    scheduledForEnrollmentLabel: 'Scheduled for Enrollment',
    enrolledLabel: 'Enrolled'
  },
  priorityCandidatesFilter: {
    firstNameLabel: 'First Name',
    lastNameLabel: 'Last Name',
    filtersMenuLabel: 'Filters',
    advancedInsuranceFiltersTitle: 'Advanced Insurance Filters'
  },
  myPatientsFilters: {
    submitSuccessMessage: 'Patients RPM reports submitted successfully',
    submitFailedTitle: 'Failed to Submit Patients RPM Reports',
    submitFailedMessage: 'Failed to submit patients RPM reports',
    closeLabel: 'Close',
    confirmSubmitAsReadyTitle: 'Confirm Submit RPM Reports as Ready',
    confirmSubmitAsReadyMessage: (monthAndYear: string) =>
      `This will submit the RPM reports for the selected patients for the month of ${monthAndYear} as ready to send. Are you sure you want to proceed?`,
    confirmSendNowTitle: 'Confirm Instantly Submit RPM Reports For Review',
    confirmSendNowMessage: (monthAndYear: string) =>
      `This will <b>instantly send</b> the RPM reports related to selected patients for the month of ${monthAndYear}. Are you sure you want to proceed?`,
    refreshEnabledDescription: 'Refreshing enrolled accounts will update and re-calculate the data, which may take some time to complete.',
    refreshDisabledDescription: 'You cannot refresh accounts that are not in the current month.',
    refreshFailedMessage: 'Could not start refreshing patient data. Please try again.',
    rpmReportsLabel: 'RPM Reports',
    scheduleForLaterLabel: 'Schedule for Later',
    sendNowLabel: 'Send Now'
  },
  myPatientsProvider: {
    unenrolledLabel: 'Unenrolled'
  },
  myPatientsDataGrid: {
    reportPreviewTitle: 'RPM Report Preview',
    noInitialVisitAppointmentMessage: 'No New Patient Visit appointment found for this patient',
    scheduleFollowUpVisitTitle: 'Schedule Follow-up Visit',
    noDataMessage: 'No data',
    unenrolledCountErrorMessage: 'Failed to get Unenrolled Patients count',
    confirmationRequiredTitle: 'Confirmation Required',
    snoozeConfirmMessage: (snoozeStatus: boolean) => `Are you sure you want to ${snoozeStatus ? 'Snooze ' : 'Unsnooze '} this patient ?`,
    snoozeFailedMessage: 'Failed to snooze priority candidates',
    scoreUnavailableMessage: 'Score unavailable, try again later',
    priorityScoreUnchangedMessage: 'Priority score unchanged',
    supportingDataTitle: 'Supporting Data',
    tagPatientTooltip: 'Tag Patient',
    recalculateScoreTooltip: 'Recalculate Score',
    scheduleVisitTooltip: 'Schedule a visit',
    viewRpmSummaryReportTooltip: 'View RPM Summary Report',
    cancelScheduleForEnrollmentTooltip: 'Cancel Schedule for Enrollment',
    scheduleForEnrollmentTooltip: 'Schedule for Enrollment',
    unableToRetrievePatientDataMessage: 'Unable to retrieve patient data',
    columns: {
      nameLastFirst: 'Name (Last, First)',
      phoneNumber: 'Phone number',
      priorityLevel: 'Priority Level',
      dob: 'DOB',
      supportingData: 'Supporting Data',
      dmLikelihood: 'DM Likelihood',
      htnLikelihood: 'HTN Likelihood',
      zipcode: 'Zipcode',
      devices: 'Devices',
      lastReadingOn: 'Last Reading On',
      lastReading: 'Last Reading',
      dateEnrolled: 'Date Enrolled',
      dateUnenrolled: 'Date Unenrolled',
      lastMedRecOn: 'Last MedRec On',
      initialVisitOn: 'New Patient Visit On',
      followUpVisit: 'Follow-up Visit',
      providerApprovals: 'Provider Approvals',
      lastContactedAt: 'Last Contacted At',
      lastActivityAt: 'Last Activity At',
      monthlyTotalReadings: 'Monthly Total Readings',
      monthlyTimeSpent: 'Monthly Time Spent',
      lastLogBp: 'Last Log BP',
      lastLogBg: 'Last Log BG',
      lastLogHr: 'Last Log HR',
      report: 'Report',
      actions: 'Actions'
    }
  },
  advancedInsuranceFilters: {
    tabsAriaLabel: 'insurance plan categories'
  },
  priorityCandidatesCsvExport: {
    downloadExportsLabel: 'Download Exports',
    reportFallbackLabel: 'Report'
  },
  batchPriorityScoreButton: {
    fetchStatusErrorMessage: 'Unable to fetch recalculation status',
    recalculatedMessage: (failed: number) =>
      failed > 0 ? `Priority scores recalculated (${failed} failed)` : 'Priority scores recalculated',
    batchFailedMessage: 'Batch recalculation failed',
    unableToStartMessage: 'Unable to start recalculation',
    unableToStartRetryMessage: 'Unable to start recalculation, try again later',
    recalculatingLabel: 'Recalculating…',
    recalcScoresLabel: 'Recalc Scores'
  },
  consentDocuments: {
    annualReconsentTitle: 'Annual re-consent',
    annualReconsentMessage: 'Consent forms must be renewed every year. Please re-sign all consent forms for this visit.',
    historyButton: 'Consent History'
  },
  consentHistory: {
    title: 'Consent History',
    subtitle: 'All signed versions of the consent forms and the visit each was signed in.',
    loadErrorMessage: 'Failed to load consent history',
    viewErrorMessage: 'Failed to load document',
    emptyMessage: 'No signed consent forms yet.',
    latestLabel: 'Latest',
    viewLabel: 'View',
    signedOnLabel: (date: string) => `Signed ${date}`,
    signedOnByLabel: (signedOn: string, name: string) => `${signedOn} · ${name}`,
    signaturesLabel: (count: number) => (count === 1 ? '1 signature' : `${count} signatures`),
    groupByVisitLabel: 'Visit',
    groupByDocumentLabel: 'Document Type',
    visitLabels: {
      RPM_INITIAL: 'New Patient Visit',
      RPM_FOLLOWUP: 'Follow-up Visit',
      CCM_ENCOUNTER: 'CCM Visit'
    },
    visitOnDateLabel: (visitLabel: string, date: string) => `${visitLabel} · ${date}`,
    outsideVisitLabel: 'Signed outside a visit',
    beforeTrackingLabel: 'Before visit tracking',
    closeLabel: 'Close'
  },
  aiReferralNote: {
    title: 'AI summary',
    mostRecentLabel: 'Most recent record',
    generatingLabel: 'Generating…',
    generatingHint: 'Usually ready within a minute of the record landing. This panel refreshes itself.',
    generatedAtLabel: (when: string) => `Generated ${when}`,
    copyLabel: 'Copy note',
    copiedMessage: 'Summary copied',
    copyFailedMessage: 'Could not copy the summary',
    openSourceLabel: 'Open source PDF',
    unavailableMessage: 'No clinical content to summarize in this record.',
    unavailableHint: 'The bundle arrived empty. The source document is still available.',
    failedMessage: 'Could not generate the note for this record.',
    failedHint: 'The source document is unaffected and still available.',
    generateLabel: 'Generate note',
    retryLabel: 'Try again',
    // Says the quiet part out loud: nothing was generated behind the scenes, so an absent note is
    // not a failure and pressing the button is the expected thing to do.
    generateTooltip: 'Summarize this record with AI. Records from before this feature are only summarized when you ask.',
    generateStartedMessage: 'Generating the summary. It will appear shortly.',
    generateFailedMessage: 'Could not start generating the summary',
    disclaimer:
      'AI-generated from an external record. External records are often incomplete — verify against the source document before clinical use.',
    showFullLabel: 'Show full note',
    showLessLabel: 'Show less',
    truncatedHint: 'The note is shortened here — expand to read every section.',
    columnHeader: 'AI note',
    viewLabel: 'View',
    shownAboveLabel: 'Shown above',
    noContentLabel: 'No clinical content',
    dialogSubtitle: (importedOn: string) => `Record imported ${importedOn}`,
    dialogSubtitleWithNote: (importedOn: string, generatedOn: string) => `Record imported ${importedOn} · note generated ${generatedOn}`
  },
  pcpReportDates: {
    sectionLabel: 'Shared Reports Status',
    deliveredLabel: 'Last Delivered',
    deliveredDefinition: "The last time a monthly RPM report reached at least one of the patient's providers.",
    attemptedLabel: 'Last Attempt',
    attemptedDefinition: "The last time a monthly RPM report was sent to the patient's providers, whether or not it was delivered.",
    notDeliveredWarning:
      'The most recent attempt was not delivered. It failed or is still waiting on the fax; see the Failed tab in Reports & Communication.'
  },
  programUnenroll: {
    unenrollLabel: 'Unenroll',
    unenrollFromRpmLabel: 'Unenroll from RPM',
    unenrollFromRpmAndCcmLabel: 'Unenroll from RPM & CCM',
    unenrollFromCcmLabel: 'Unenroll from CCM'
  },
  rpmUnenrollDialog: {
    title: 'Are you sure you want to unenroll this patient?',
    startStepLabel: 'Start',
    information:
      'You are about to unenroll this patient from the RPM program. This action involves a few steps and, once completed, will permanently remove the patient from active monitoring. All previously collected data will be archived and will no longer be accessible in real-time.',
    keepsCcm: 'The patient stays enrolled in CCM.',
    confirmation: 'Please confirm that you wish to proceed with this unenrollment.',
    continueLabel: 'Continue'
  },
  reasonForVisit: {
    chronicCareSupportLabel: 'Additional support with Chronic Conditions',
    updatedMessage: 'Reason for Visit updated',
    updateFailedMessage: 'Unable to update the Reason for Visit. Please try again.'
  }
} as const;
